import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Comments from "./components/Comments";
import ModeratorInbox from "./components/ModeratorInbox";
import Campaigns from "./components/Campaigns";
import Configuration from "./components/Configuration";
import Reactions from "./components/Reactions";
import Statistics from "./components/Statistics";
import Intelligence from "./components/Intelligence";
import Activity from "./components/Activity";

const API = process.env.REACT_APP_API || "https://www.vilaro.ro/api";

axios.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============================================================================
// SIGLA VILARO
// Vectorial, scrisa direct in pagina ca sa nu depinda de un fisier extern.
// `light` = fond deschis (litere negre). Rosul #E8110E nu se schimba niciodata —
// e singurul accent de culoare al marcii.
// ============================================================================
function SiglaVilaro({ light = false, style = {} }) {
  const c = light ? "#111318" : "#fff";
  return (
    <svg viewBox="0 0 392 96" aria-label="Vilaro" style={style}>
      <path fill={c} d="M4 6 h30 l34 84 h-26 z" />
      <path fill="#E8110E" d="M104 6 L58 78 l-6-32 z" />
      <g fill={c}>
        <rect x="96" y="34" width="22" height="56" />
        <path d="M130 34 h22 v38 h34 v18 h-56 z" />
        <path d="M206 34 h26 l30 56 h-24 l-5-10 h-28 l-5 10 h-24 z M217 62 h16 l-8-16 z" fillRule="evenodd" />
        <path d="M270 34 h40 c14 0 22 7 22 18 0 8-4 13-11 16 l13 22 h-25 l-11-18 h-6 v18 h-22 z M292 49 v11 h14 c4 0 6-2 6-5.5 0-3.5-2-5.5-6-5.5 z" fillRule="evenodd" />
        <path d="M346 34 h34 c8 0 12 4 12 12 v34 c0 8-4 12-12 12 h-34 c-8 0-12-4-12-12 v-34 c0-8 4-12 12-12 z M352 52 h22 c2 0 3 1 3 3 v14 c0 2-1 3-3 3 h-22 c-2 0-3-1-3-3 v-14 c0-2 1-3 3-3 z" fillRule="evenodd" />
      </g>
    </svg>
  );
}

// ============================================================================
// FUNDAL — fluxul de comentarii clasificate
// Puncte care coboara incet. La jumatatea drumului trec printr-o linie abia
// vizibila si capata culoarea clasificarii: verde susținător, chihlimbar critic,
// rosu troll. Proportiile sunt cele reale de pe postarile virale.
// ============================================================================
function FluxComentarii() {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let W, H, puncte = [], linieY, raf;

    const CULORI = [
      { c: "34,197,94", p: 0.34 },   // sustinatori
      { c: "245,158,11", p: 0.22 },  // critici
      { c: "232,17,14", p: 0.44 },   // troli
    ];

    const alegeCuloare = () => {
      const r = Math.random();
      let acc = 0;
      for (const k of CULORI) { acc += k.p; if (r < acc) return k.c; }
      return CULORI[0].c;
    };

    const punctNou = (sus) => ({
      x: Math.random() * W,
      y: sus ? -20 - Math.random() * H : Math.random() * H,
      r: 1.4 + Math.random() * 2.2,
      v: 0.18 + Math.random() * 0.45,
      culoare: alegeCuloare(),
      drift: (Math.random() - 0.5) * 0.12,
    });

    const init = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = cv.clientWidth; H = cv.clientHeight;
      cv.width = W * dpr; cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      linieY = H * 0.52;
      const câte = Math.round((W * H) / 13000);
      puncte = Array.from({ length: Math.min(220, Math.max(40, câte)) }, () => punctNou(false));
    };

    const cadru = () => {
      ctx.clearRect(0, 0, W, H);

      const grad = ctx.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0, "rgba(255,255,255,0)");
      grad.addColorStop(0.5, "rgba(255,255,255,.055)");
      grad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, linieY, W, 1);

      for (const p of puncte) {
        p.y += p.v;
        p.x += p.drift;
        if (p.y > H + 20) Object.assign(p, punctNou(true));

        const t = Math.min(1, Math.max(0, (p.y - linieY) / 46));
        const a = 0.07 + t * 0.30;
        ctx.fillStyle = t < 0.02 ? `rgba(255,255,255,${a})` : `rgba(${p.culoare},${a})`;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(cadru);
    };

    init();
    window.addEventListener("resize", init);
    raf = requestAnimationFrame(cadru);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", init);
    };
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1, overflow: "hidden" }}>
      <canvas ref={ref} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ingust, setIngust] = useState(window.innerWidth < 880);

  useEffect(() => {
    const h = () => setIngust(window.innerWidth < 880);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("username", res.data.username);
      localStorage.setItem("role", res.data.role);
      onLogin(res.data.username, res.data.role);
    } catch (err) {
      setError("Utilizator sau parolă incorectă.");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const stilInput = {
    width: "100%",
    marginBottom: 18,
    padding: "12px 14px",
    fontSize: 15,
    color: "#fff",
    background: "rgba(255,255,255,.04)",
    border: "1px solid rgba(255,255,255,.1)",
    borderRadius: 10,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color .15s, background .15s",
    fontFamily: "inherit",
  };

  const stilEticheta = {
    display: "block",
    marginBottom: 7,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,.38)",
  };

  const marca = (light) => (
    <div style={{ display: "flex", alignItems: "center", gap: light ? 14 : 18, marginBottom: light ? 26 : 38 }}>
      <SiglaVilaro light={false} style={{ height: light ? 22 : 32, opacity: light ? 0.9 : 0.85 }} />
      <span
        style={{
          paddingLeft: light ? 14 : 18,
          borderLeft: `1px solid rgba(255,255,255,${light ? ".16" : ".22"})`,
          fontSize: light ? 13 : 18,
          fontWeight: 600,
          letterSpacing: ".01em",
          color: `rgba(255,255,255,${light ? ".6" : ".75"})`,
        }}
      >
        SocialMediaBot
      </span>
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8vw",
        padding: 24,
        background: "#0a0a0f",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .smb-login-input:focus {
          border-color: rgba(255,255,255,.28) !important;
          background: rgba(255,255,255,.06) !important;
        }
        .smb-login-input::placeholder { color: rgba(255,255,255,.22); }
        .smb-login-btn:hover:not(:disabled) { filter: brightness(1.1); }
        .smb-login-btn:active:not(:disabled) { transform: translateY(1px); }
      `}</style>

      <FluxComentarii />

      {/* Vignette — tine atentia in centru fara sa taie fundalul brusc */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse 80% 60% at 50% 50%, transparent 0%, rgba(10,10,15,.55) 70%, rgba(10,10,15,.85) 100%)",
        }}
      />

      {/* ============ STANGA ============ */}
      {!ingust && (
        <div style={{ position: "relative", maxWidth: 460, color: "#fff", zIndex: 3 }}>
          {marca(false)}

          <h2 style={{ margin: "0 0 16px", fontSize: 38, lineHeight: 1.12, fontWeight: 700, letterSpacing: "-.025em", color: "#fff" }}>
            Fiecare voce, la locul ei.
          </h2>
          <p style={{ margin: "0 0 28px", fontSize: 18, fontWeight: 500, color: "rgba(255,255,255,.55)", lineHeight: 1.5 }}>
            Comentariile intră pe pagină. Ies clasificate.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 13, marginTop: 6 }}>
            {[
              ["#22c55e", "34,197,94", "Susținători", "recunoscuți, nu blocați din greșeală"],
              ["#f59e0b", "245,158,11", "Critici", "separați de atacuri, tratați cu argument"],
              ["#E8110E", "232,17,14", "Troli", "filtrați automat, cu coordonarea detectată"],
            ].map(([culoare, rgb, titlu, text]) => (
              <div key={titlu} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, color: "rgba(255,255,255,.42)" }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: culoare,
                    boxShadow: `0 0 12px rgba(${rgb},.5)`,
                  }}
                />
                <span>
                  <b style={{ color: "rgba(255,255,255,.72)", fontWeight: 600 }}>{titlu}</b> — {text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ CUTIE ============ */}
      <form
        onSubmit={handleLogin}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 410,
          background: "rgba(23,23,23,.72)",
          backdropFilter: "blur(24px) saturate(140%)",
          WebkitBackdropFilter: "blur(24px) saturate(140%)",
          border: "1px solid rgba(255,255,255,.08)",
          borderRadius: 18,
          padding: "36px 34px 32px",
          boxShadow: "0 24px 70px rgba(0,0,0,.6)",
          zIndex: 3,
        }}
      >
        {ingust && marca(true)}

        <h1 style={{ margin: "0 0 5px", fontSize: 22, color: "#fff", letterSpacing: "-.015em", fontWeight: 600 }}>
          Autentificare
        </h1>
        <p style={{ margin: "0 0 28px", fontSize: 13.5, color: "rgba(255,255,255,.38)" }}>
          Panoul de administrare
        </p>

        {error && (
          <div
            style={{
              background: "rgba(232,17,14,.12)",
              color: "#ff8b89",
              border: "1px solid rgba(232,17,14,.25)",
              borderRadius: 10,
              padding: "11px 14px",
              fontSize: 13.5,
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        <label htmlFor="smb-u" style={stilEticheta}>Utilizator</label>
        <input
          id="smb-u"
          className="smb-login-input"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck="false"
          autoFocus
          placeholder="numele tău de utilizator"
          style={stilInput}
        />

        <label htmlFor="smb-p" style={stilEticheta}>Parola</label>
        <input
          id="smb-p"
          className="smb-login-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          placeholder="••••••••"
          style={stilInput}
        />

        <button
          type="submit"
          disabled={loading}
          className="smb-login-btn"
          style={{
            width: "100%",
            padding: 13,
            fontSize: 15,
            fontWeight: 600,
            color: "#fff",
            background: "#E8110E",
            border: 0,
            borderRadius: 10,
            cursor: loading ? "default" : "pointer",
            marginTop: 6,
            opacity: loading ? 0.5 : 1,
            boxShadow: loading ? "none" : "0 6px 20px rgba(232,17,14,.28)",
            transition: "filter .15s, transform .1s",
            fontFamily: "inherit",
          }}
        >
          {loading ? "Se verifică…" : "Intră"}
        </button>
      </form>

      <div
        style={{
          position: "absolute",
          bottom: 22,
          left: 0,
          right: 0,
          textAlign: "center",
          color: "rgba(255,255,255,.22)",
          fontSize: 12,
          letterSpacing: ".03em",
          zIndex: 3,
        }}
      >
        Vilaro · monitorizare comunitate Facebook
      </div>
    </div>
  );
}

function Sidebar({ activeTab, setActiveTab, username, role, onLogout, isOpen, onClose }) {
  const allTabs = [
    { id: "campanii", icon: "📢", label: "Campanii", sub: "Pagini Proprii", roles: ["admin", "operator"] },
    { id: "comentarii", icon: "💬", label: "Comentarii", sub: "Postari Proprii", roles: ["admin", "operator"] },
    { id: "moderator", icon: "🛡️", label: "Moderator", sub: "Raspuns rapid", roles: ["admin", "operator", "moderator"] },
    { id: "reactii", icon: "⚔️", label: "Reactii", sub: "Postari Externe", roles: ["admin", "operator"] },
    { id: "statistici", icon: "📊", label: "Statistici", sub: "", roles: ["admin", "operator"] },
    { id: "intelligence", icon: "🕵️", label: "Intelligence", sub: "", roles: ["admin", "operator"] },
    { id: "activitate", icon: "📋", label: "Activitate", sub: "", roles: ["admin"] },
    { id: "configurare", icon: "⚙️", label: "Configurare", sub: "", roles: ["admin"] },
  ];

  const tabs = allTabs.filter(t => t.roles.includes(role));

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          style={{background: "rgba(0,0,0,0.6)"}}
          onClick={onClose}
        />
      )}
      <div
        className="fixed left-0 top-0 h-full flex flex-col z-40 transition-transform duration-300"
        style={{
          width: "220px",
          background: "linear-gradient(180deg, #0d0d1a 0%, #0a0a0f 100%)",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          transform: isOpen ? "translateX(0)" : "translateX(-220px)",
        }}
      >
        <div className="p-5 mb-2">
          <div className="flex items-center gap-3">
            <div style={{background: "linear-gradient(135deg, #6366f1, #8b5cf6)"}} className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">S</div>
            <div>
              <div className="text-white font-bold text-sm leading-tight">SocialMediaBot</div>
              <div className="text-xs" style={{color: "rgba(255,255,255,0.3)"}}>Dashboard</div>
            </div>
          </div>
        </div>

        <div className="px-3 flex-1">
          <div className="text-xs font-semibold mb-3 px-2" style={{color: "rgba(255,255,255,0.25)", letterSpacing: "0.08em"}}>NAVIGARE</div>
          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left"
                style={activeTab === tab.id ? {
                  background: "linear-gradient(135deg, rgba(99,102,241,0.25), rgba(139,92,246,0.15))",
                  border: "1px solid rgba(99,102,241,0.3)",
                  color: "#a5b4fc"
                } : {
                  color: "rgba(255,255,255,0.45)",
                  border: "1px solid transparent"
                }}>
                <span className="text-base flex-shrink-0">{tab.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="leading-tight">{tab.label}</div>
                  {tab.sub && <div className="text-xs leading-tight mt-0.5" style={{color: activeTab === tab.id ? "rgba(165,180,252,0.6)" : "rgba(255,255,255,0.25)"}}>{tab.sub}</div>}
                </div>
                {activeTab === tab.id && (
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background: "#6366f1"}}></div>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-3 mt-auto">
          <div className="rounded-xl p-3" style={{background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)"}}>
            <div className="flex items-center gap-2 mb-2">
              <div style={{background: "linear-gradient(135deg, #6366f1, #8b5cf6)"}} className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0">
                {username?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-medium truncate">{username}</div>
                <div className="text-xs" style={{color: "rgba(255,255,255,0.3)"}}>{role === "admin" ? "Administrator" : role === "moderator" ? "Moderator" : "Operator"}</div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="w-full text-xs py-1.5 rounded-lg transition-all"
              style={{color: "rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.04)"}}>
              Deconectare
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Dashboard({ username, role, onLogout }) {
  const [activeTab, setActiveTab] = useState(role === "moderator" ? "moderator" : "campanii");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tabLabels = {
    campanii: "📢 Campanii",
    comentarii: "💬 Comentarii",
    reactii: "⚔️ Reactii",
    statistici: "📊 Statistici",
    intelligence: "🕵️ Intelligence",
    configurare: "⚙️ Configurare",
  };

  return (
    <div className="min-h-screen" style={{background: "#0a0a0f", color: "white"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { font-family: 'Inter', sans-serif; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
      `}</style>

      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        username={username}
        role={role}
        onLogout={onLogout}
        isOpen={isMobile ? sidebarOpen : true}
        onClose={() => setSidebarOpen(false)}
      />

      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3"
          style={{background: "rgba(10,10,15,0.95)", borderBottom: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(10px)"}}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)"}}>
            <span className="text-white text-lg">☰</span>
          </button>
          <span className="text-white font-semibold text-sm">{tabLabels[activeTab]}</span>
          <div style={{background: "linear-gradient(135deg, #6366f1, #8b5cf6)"}} className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold">
            {username?.charAt(0).toUpperCase()}
          </div>
        </div>
      )}

      <div style={{marginLeft: isMobile ? "0" : "220px", paddingTop: isMobile ? "60px" : "0"}}>
        <div className={isMobile ? "px-4 py-4" : "px-8 py-6"}>
          {activeTab === "campanii" && <Campaigns />}
          {activeTab === "comentarii" && <Comments />}
          {activeTab === "moderator" && <ModeratorInbox />}
          {activeTab === "reactii" && <Reactions />}
          {activeTab === "statistici" && <Statistics />}
          {activeTab === "intelligence" && <Intelligence />}
          {activeTab === "activitate" && role === "admin" && <Activity />}
          {activeTab === "configurare" && role === "admin" && <Configuration />}
          {activeTab === "configurare" && role !== "admin" && (
            <div className="text-center py-24" style={{color:"rgba(255,255,255,0.25)"}}>
              <p className="text-4xl mb-4">🔒</p>
              <p className="text-lg font-semibold text-white mb-2">Acces restrictionat</p>
              <p className="text-sm">Doar administratorii pot accesa Configurarea.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [username, setUsername] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUsername = localStorage.getItem("username");
    const savedRole = localStorage.getItem("role");
    if (token && savedUsername) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUsername(savedUsername);
        setRole(res.data.role || savedRole || "operator");
      }).catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        localStorage.removeItem("role");
      });
    }
  }, []);

  const handleLogin = (username, role) => {
    setUsername(username);
    setRole(role);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    setUsername(null);
    setRole(null);
  };

  if (!username) return <Login onLogin={handleLogin} />;
  return <Dashboard username={username} role={role} onLogout={handleLogout} />;
}
